/**
 * ConversationListRow — single conversation row in the left panel.
 * Uses .conv-row CSS classes defined in inbox/index.html.erb <style>.
 *
 * Props:
 *   conversation {object}  — conversation JSON from email_to_support_json
 *   isActive     {bool}    — currently selected
 *   onClick      {function}
 */

import { h, Fragment } from 'preact'
import { useState } from 'preact/hooks'
import { timeAgoShort, avatarColor } from './utils.js'
import { StatusBadge } from './StatusBadge.jsx'

export function ConversationListRow({ conversation, isActive, onClick }) {
  const isUnread   = conversation.unread
  const color      = avatarColor(conversation.from_name || conversation.from?.name)
  const initial    = ((conversation.from_name || conversation.from?.name || '?').trim()[0] || '?').toUpperCase()
  const timeStr    = timeAgoShort(conversation.updated_at || conversation.created_at)
  const senderName = conversation.from_name || conversation.from?.name || conversation.from?.email || '?'

  // Gravatar fallback: show initial on image load error
  const [imgFailed, setImgFailed] = useState(false)
  const gravatarUrl = conversation.from_avatar_url

  return (
    <div
      id={`support-conv-row-${conversation.id}`}
      class={[
        'conv-row',
        isActive  ? 'active'  : '',
        isUnread  ? 'unread'  : '',
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick?.()}
    >
      {isUnread && <span class="unread-dot" aria-hidden="true" />}

      {/* Sender avatar — Gravatar if available, coloured initial as fallback */}
      <div
        class="conv-avatar"
        style={(!gravatarUrl || imgFailed) ? { background: color } : {}}
      >
        {gravatarUrl && !imgFailed
          ? <img
              src={gravatarUrl}
              alt={senderName}
              class="conv-avatar-img"
              loading="lazy"
              onError={() => setImgFailed(true)}
            />
          : initial
        }
      </div>

      {/* Content */}
      <div class="conv-content">
        <div class="conv-meta">
          <span class="conv-sender">{senderName}</span>
          <span class="conv-time">{timeStr}</span>
        </div>
        <div class="conv-subject">
          {conversation.subject || '(Nincs tárgy)'}
        </div>
        <div class="conv-snippet">{conversation.snippet}</div>
        <div class="conv-badges">
          <StatusBadge status={conversation.status} size="sm" />
          {conversation.responsible && (
            <div
              class="assignee-btn assigned assignee-btn-sm"
              title={conversation.responsible.name}
            >
              {conversation.responsible.avatar_url
                ? <img
                    src={conversation.responsible.avatar_url}
                    alt={conversation.responsible.name}
                    class="assignee-btn-sm-img"
                    loading="lazy"
                  />
                : (conversation.responsible.name || '?')[0]?.toUpperCase()
              }
            </div>
          )}
          {conversation.mailbox_name && (
            <span class="conv-mailbox-badge" style={{
              background: avatarColor(String(conversation.mailbox_id)) + '22',
              color: avatarColor(String(conversation.mailbox_id)),
            }}>
              {conversation.mailbox_name}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
